/**
 * CaptchaService - ALTCHA PoW CAPTCHA Integration (sl-wwta.3.2)
 *
 * Provides a PoW challenge solution for auth requests via verifyToken$.
 * Uses ALTCHA (MIT, self-hosted) instead of Cloudflare Turnstile.
 *
 * Flow:
 * 1. On revalidate(): Fetch challenge from /api/captcha/challenge
 * 2. Solve PoW challenge using altcha-lib's solveChallenge()
 * 3. Emit base64-encoded solution to verifyToken$
 * 4. Auth requests include solution as x-altcha-payload header
 *
 * The challenge is solved in the background — users see nothing.
 */
import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
} from '@toeverything/infra';
import { exhaustMap, tap } from 'rxjs';

import type { FetchService } from './fetch';
import { solveCaptchaChallenge } from './solve-captcha';

export class CaptchaService extends Service {
  /** Whether CAPTCHA is required (always true — ALTCHA runs invisibly) */
  needCaptcha$ = new LiveData<boolean>(true);
  /** Not used with ALTCHA (no site key needed) — kept for API compatibility */
  siteKey$ = new LiveData<string | null>(null);
  isLoading$ = new LiveData(false);
  /** Base64-encoded ALTCHA solution payload */
  verifyToken$ = new LiveData<string | undefined>(undefined);
  error$ = new LiveData<any | undefined>(undefined);

  constructor(private readonly fetchService: FetchService) {
    super();
  }

  revalidate = effect(
    exhaustMap(() => {
      return fromPromise(async signal => {
        // Fetch ALTCHA challenge from backend
        const res = await this.fetchService.fetch('/api/captcha/challenge', {
          signal,
        });
        const challenge = (await res.json()) as {
          algorithm: string;
          challenge: string;
          salt: string;
          signature: string;
          maxnumber: number;
          requireCaptcha: boolean;
        };

        const payload = await solveCaptchaChallenge(challenge, signal);

        return { payload, requireCaptcha: challenge.requireCaptcha };
      }).pipe(
        tap(({ payload }) => {
          // Always set needCaptcha to true — controls the Captcha Gate in
          // sign-in-with-email.tsx that renders <Captcha /> to solve the PoW.
          // The gate must stay active until verifyToken is set, even if the
          // backend says requireCaptcha: false (registration ALWAYS needs it).
          this.needCaptcha$.next(true);
          this.verifyToken$.next(payload);
        }),
        catchErrorInto(this.error$),
        onStart(() => {
          this.needCaptcha$.next(true);
          this.verifyToken$.next(undefined);
          this.isLoading$.next(true);
        }),
        onComplete(() => this.isLoading$.next(false))
      );
    })
  );
}
